import React, { useEffect, useState } from "react";
import { api, fmtGNF, openAuthedFile } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { CheckCircle2, XCircle, FileText, Receipt, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const STATUS_UI = {
  pending: { label: "En attente", color: "bg-yellow-100 text-yellow-800" },
  validated: { label: "Validé", color: "bg-green-100 text-green-800" },
  rejected: { label: "Rejeté", color: "bg-red-100 text-red-800" },
};

const METHOD_LABELS = {
  bank_transfer: "Virement bancaire",
  bank_deposit: "Dépôt au guichet",
  orange_money: "Orange Money",
  mobile_money_manual: "Mobile Money (manuel)",
  other: "Autre",
};

export default function ReceiptsValidationPage() {
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState("pending");
  const [previewUrl, setPreviewUrl] = useState(null);

  // Aperçu authentifié de la preuve (l'URL directe échoue avec "Token requis")
  useEffect(() => {
    setPreviewUrl(null);
    if (!selected?.file_path) return;
    let revoked = false;
    api.get(`/erp/parent-receipts/${selected.id}/file`, { responseType: "blob" })
      .then(({ data, headers }) => {
        if (revoked) return;
        const blob = new Blob([data], { type: headers["content-type"] || "application/octet-stream" });
        setPreviewUrl(URL.createObjectURL(blob));
      })
      .catch(() => {});
    return () => { revoked = true; };
  }, [selected?.id]);

  const load = async () => {
    try {
      const { data } = await api.get("/erp/parent-receipts", { params: filter ? { status: filter } : {} });
      setRows(data);
    } catch { toast.error("Erreur"); }
  };
  useEffect(() => { load(); }, [filter]);

  const decide = async (validated) => {
    if (!selected) return;
    setSaving(true);
    try {
      await api.post(`/erp/parent-receipts/${selected.id}/validate`, {
        validated, validation_note: note || undefined,
      });
      toast.success(validated ? "Reçu validé — paiement confirmé" : "Reçu rejeté");
      setSelected(null);
      setNote("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="receipts-validation-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="chip mb-2"><Receipt className="h-3 w-3" /><span>VALIDATION FINANCIÈRE</span></div>
          <h1 className="font-heading font-extrabold text-3xl text-[#1E3A8A]">Validation des reçus bancaires</h1>
          <p className="text-slate-500 mt-1">Reçus envoyés par les parents à valider avant génération du reçu officiel.</p>
        </div>
        <Button variant="outline" onClick={load}><RefreshCw className="h-4 w-4 mr-2" />Actualiser</Button>
      </div>

      <div className="flex gap-2">
        {["pending", "validated", "rejected"].map(s => (
          <button key={s}
                  onClick={() => setFilter(s)}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${filter === s ? "bg-[#1E3A8A] text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"}`}
                  data-testid={`filter-${s}`}>
            {STATUS_UI[s].label}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-3 text-left">Date</th>
                <th className="px-4 py-3 text-left">Parent ID</th>
                <th className="px-4 py-3 text-left">Banque / Réf.</th>
                <th className="px-4 py-3 text-right">Montant</th>
                <th className="px-4 py-3 text-center">Statut</th>
                <th className="px-4 py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const st = STATUS_UI[r.status] || STATUS_UI.pending;
                return (
                  <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`recv-row-${r.id}`}>
                    <td className="px-4 py-2 text-xs text-slate-500">{new Date(r.created_at).toLocaleString("fr-FR")}</td>
                    <td className="px-4 py-2 font-mono text-xs">{r.parent_id.slice(0, 8)}...</td>
                    <td className="px-4 py-2 text-xs">{r.bank_name}<br /><span className="text-slate-400">{r.reference_number}</span></td>
                    <td className="px-4 py-2 text-right tabular-nums font-semibold">{fmtGNF(r.paid_amount)}</td>
                    <td className="px-4 py-2 text-center"><span className={`text-xs px-2 py-1 rounded-full font-semibold ${st.color}`}>{st.label}</span></td>
                    <td className="px-4 py-2 text-right">
                      <Button size="sm" variant="outline" onClick={() => setSelected(r)} data-testid={`review-${r.id}`}>Examiner</Button>
                    </td>
                  </tr>
                );
              })}
              {rows.length === 0 && <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-400">Aucun reçu</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>Examen du reçu bancaire</DialogTitle></DialogHeader>
          {selected && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-3 text-sm">
                <div><span className="text-slate-500">Parent :</span> <code className="text-xs">{selected.parent_id}</code></div>
                <div><span className="text-slate-500">Mode :</span> {selected.method === "orange_money"
                  ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-orange-100 text-[#FF7900] text-xs font-bold">Orange Money</span>
                  : (METHOD_LABELS[selected.method] || selected.method)}</div>
                <div><span className="text-slate-500">Banque :</span> {selected.bank_name || "—"}</div>
                <div><span className="text-slate-500">Référence :</span> <code className="text-xs">{selected.reference_number || "—"}</code></div>
                <div><span className="text-slate-500">Montant déclaré :</span> <strong className="text-[#1E3A8A]">{fmtGNF(selected.paid_amount)}</strong></div>
                <div><span className="text-slate-500">Date paiement :</span> {selected.paid_date}</div>
                {selected.notes && <div><span className="text-slate-500">Note parent :</span> {selected.notes}</div>}
                {selected.validation_note && <div className="bg-slate-50 p-2 rounded"><span className="text-slate-500 text-xs">Note validation :</span> {selected.validation_note}</div>}
                {selected.status === "pending" && (
                  <div>
                    <label className="text-xs uppercase text-slate-500">Note de validation (optionnelle)</label>
                    <Textarea value={note} onChange={e => setNote(e.target.value)} rows={3} placeholder="Motif si rejet, ou commentaire de validation..." data-testid="val-note" />
                  </div>
                )}
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-lg overflow-hidden">
                <div className="px-3 py-2 bg-slate-100 text-xs font-semibold text-slate-600 flex items-center gap-2"><FileText className="h-4 w-4" />Preuve téléversée</div>
                {!selected.content_type ? (
                  <div className="p-8 text-center text-sm text-slate-500" data-testid="no-file-panel">
                    <div className="text-[#FF7900] font-bold mb-1">Paiement Orange Money — aucun fichier joint</div>
                    Vérifiez l&apos;ID de transaction <code className="font-mono text-xs bg-white px-1 rounded border">{selected.reference_number || "—"}</code> dans votre relevé marchand Orange Money avant de valider.
                  </div>
                ) : selected.content_type?.startsWith("image/") ? (
                  previewUrl ? (
                    <img src={previewUrl} alt="Reçu" className="w-full h-auto max-h-[500px] object-contain bg-white" />
                  ) : (
                    <div className="p-8 text-center text-slate-400 text-sm">Chargement de la preuve...</div>
                  )
                ) : (
                  <button type="button"
                     onClick={() => openAuthedFile(`/erp/parent-receipts/${selected.id}/file`).catch(() => toast.error("Fichier indisponible"))}
                     className="block w-full p-8 text-center text-[#1E3A8A] hover:underline"
                     data-testid="open-proof-pdf">
                    <FileText className="h-12 w-12 mx-auto mb-2" />
                    Ouvrir le fichier PDF
                  </button>
                )}
              </div>
            </div>
          )}
          {selected?.status === "pending" && (
            <DialogFooter>
              <Button variant="destructive" onClick={() => decide(false)} disabled={saving} className="gap-2" data-testid="btn-reject">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><XCircle className="h-4 w-4" />Rejeter</>}
              </Button>
              <Button onClick={() => decide(true)} disabled={saving} className="bg-[#166534] hover:bg-[#14532d] text-white gap-2" data-testid="btn-validate">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CheckCircle2 className="h-4 w-4" />Valider et confirmer le paiement</>}
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
